import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  resolveToSecurityGroups,
  getSearchResolutionDescription,
  type DiscoveryData,
  type SearchResolutionResult,
} from './networkSecuritySearchUtils';

export interface NetworkSecurityFinding {
  id: string;
  ruleId: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  resourceType: string;
  resourceId: string;
  resourceName: string;
  vpcId: string;
  visualNodeId: string;
  ociId?: string;
  title: string;
  description: string;
  violatingRule: {
    direction: 'inbound' | 'outbound';
    protocol: string;
    portRange: string;
    source: string;
    ruleNumber?: number;
  };
  remediation: {
    description: string;
    awsCli?: string;
    ociCli?: string;
    terraform?: string;
  };
  affectedResources: string[];
  frameworks: string[];
}

export interface NetworkSecurityResults {
  scanTimestamp?: string;
  scanDate?: string;
  provider: string;
  score: number;
  summary: { critical: number; high: number; medium: number; low: number; total?: number };
  totalFindings?: number;
  findings: NetworkSecurityFinding[];
}

interface AccountInfo {
  id: string;
  provider: string;
  region?: string;
  accountId?: string;
  discoveryPath?: string;
}

export function useNetworkSecurity(account: AccountInfo | null, discoveryData?: DiscoveryData | null, dataBasePath = '/data/tenants/9') {
  const [enabled, setEnabled] = useState(false);
  const [results, setResults] = useState<NetworkSecurityResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState<NetworkSecurityFinding | null>(null);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);

  // Extract primitive values to avoid infinite loops from object reference changes
  const accountId = account?.id;
  const accountProvider = account?.provider;
  const accountRegion = account?.region;
  const accountDiscoveryPath = account?.discoveryPath;

  // Always load findings when account changes (for showing count in button)
  // The 'enabled' flag only controls whether the panel is shown
  useEffect(() => {
    if (!accountId) {
      setResults(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        // Build the path to the findings file based on account info
        let findingsPath = '';
        
        if (accountDiscoveryPath) {
          const relativePath = accountDiscoveryPath.replace('discovery.json', 'network-security-findings.json');
          findingsPath = `${dataBasePath}/${relativePath}`;
        } else if (accountProvider === 'aws') {
          findingsPath = `${dataBasePath}/aws/${accountId}/network-security-findings.json`;
        } else if (accountProvider === 'oci') {
          findingsPath = `${dataBasePath}/oci/${accountId}/${accountRegion}/network-security-findings.json`;
        }

        console.log('Loading security findings from:', findingsPath);
        const res = await fetch(findingsPath);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setResults(data);
        } else {
          console.warn(`Security findings not found at ${findingsPath}`);
          if (!cancelled) setResults(null);
        }
      } catch (e) {
        console.error('Error loading security findings:', e);
        if (!cancelled) setResults(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [accountId, accountProvider, accountRegion, accountDiscoveryPath, dataBasePath]);

  // Map node IDs to their highest severity finding
  const nodeSeverityMap = useMemo(() => {
    const map: Record<string, 'critical' | 'high' | 'medium' | 'low'> = {};
    if (!results) return map;
    
    const order = { critical: 0, high: 1, medium: 2, low: 3 };
    for (const f of results.findings) {
      const cur = map[f.visualNodeId];
      if (!cur || order[f.severity] < order[cur]) {
        map[f.visualNodeId] = f.severity;
      }
    }
    return map;
  }, [results]);

  // Map node IDs to finding counts
  const nodeFindingCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (!results) return map;
    
    for (const f of results.findings) {
      map[f.visualNodeId] = (map[f.visualNodeId] || 0) + 1;
    }
    return map;
  }, [results]);

  // Track which SG is being highlighted (for glow effect on all affected ENIs)
  const [highlightedSgId, setHighlightedSgId] = useState<string | null>(null);

  const toggleEnabled = useCallback(() => {
    setEnabled(e => !e);
    if (enabled) {
      setSelectedFinding(null);
      setHighlightedNodeId(null);
      setHighlightedSgId(null);
    }
  }, [enabled]);

  const selectFinding = useCallback((f: NetworkSecurityFinding | null) => {
    setSelectedFinding(f);
    setHighlightedNodeId(f?.visualNodeId || null);
  }, []);

  // Toggle highlight for a specific security group
  const toggleHighlightSg = useCallback((sgId: string) => {
    setHighlightedSgId(prev => prev === sgId ? null : sgId);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedFinding(null);
    setHighlightedNodeId(null);
    setHighlightedSgId(null);
  }, []);

  /**
   * Advanced search: Resolves a search query to related Security Groups
   * and filters findings accordingly.
   * 
   * Supports searching by:
   * - subnet-xxx: Shows findings for SGs attached to ENIs in that subnet
   * - IP address: Shows findings for SGs attached to the ENI with that IP
   * - eni-xxx: Shows findings for SGs attached to that ENI
   * - sg-xxx: Direct SG ID match
   * - acl-xxx: Shows findings for SGs in subnets associated with that NACL
   * - vpc-xxx: Shows all findings for SGs in that VPC
   * - Name search: Matches subnet names, SG names, etc.
   */
  const searchAndFilterFindings = useCallback((
    searchQuery: string
  ): {
    filteredFindings: NetworkSecurityFinding[];
    resolutionResult: SearchResolutionResult | null;
    resolutionDescription: string | null;
  } => {
    if (!searchQuery.trim() || !results) {
      return {
        filteredFindings: results?.findings || [],
        resolutionResult: null,
        resolutionDescription: null
      };
    }

    // Resolve the search query to related SG IDs using discovery data
    const resolutionResult = resolveToSecurityGroups(searchQuery, discoveryData || null);
    
    // If we got SG IDs from resolution, filter findings by those SG IDs
    if (resolutionResult.sgIds.length > 0) {
      const filteredFindings = results.findings.filter(f =>
        resolutionResult.sgIds.includes(f.resourceId)
      );
      
      const resolutionDescription = getSearchResolutionDescription(
        resolutionResult,
        searchQuery
      );
      
      return {
        filteredFindings,
        resolutionResult,
        resolutionDescription
      };
    }

    // Fallback: No resolution found, return empty but mark as resolved
    return {
      filteredFindings: [],
      resolutionResult,
      resolutionDescription: null
    };
  }, [results, discoveryData]);

  return {
    enabled,
    loading,
    results,
    selectedFinding,
    highlightedNodeId,
    highlightedSgId,
    nodeSeverityMap,
    nodeFindingCountMap,
    toggleEnabled,
    toggleHighlightSg,
    selectFinding,
    clearSelection,
    setHighlightedNodeId,
    // Advanced search
    searchAndFilterFindings,
    discoveryData,
  };
}

